import React, { ReactNode, useCallback, useState } from 'react';
import { AxiosError } from 'axios';
import { useConfigService } from '@thd-nucleus/experience-context';
import { accountApi, CredentialsMissingError } from '../../api/common/account';
import { IRewardsAndOffers } from '../../models/b2b/rewards-and-offers';
import { TErrorMessage } from '../../models/common/alert-messages';
import { ERR_PRICING_BOOK } from '../../constants';
import { newrelic, PRICING_BOOK_PAGE_LOAD } from '../../utils/newrelic';
import { extractRewardsError } from '../../utils/extract-rewards-error';

interface IProviderProps {
  children: ReactNode;
}

export interface IRewardsAndOffersProviderValues {
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  rewardsAndOffers: IRewardsAndOffers | null;
  getRewardsAndOffersData: () => Promise<void>;
  generalError: TErrorMessage | null;
  setGeneralError: React.Dispatch<React.SetStateAction<TErrorMessage | null>>;
  showV2PriceBook: boolean;
  isRegistrationEntryPointEnabled: boolean;
}

const RewardsAndOffersContext =
  React.createContext<IRewardsAndOffersProviderValues | null>(null);

const RewardsAndOffersProvider = ({ children }: IProviderProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [generalError, setGeneralError] = useState<null | TErrorMessage>(null);
  const [rewardsAndOffers, setRewardsAndOffers] =
    useState<IRewardsAndOffersProviderValues['rewardsAndOffers']>(null);
  const [isChildAccount, setIsChildAccount] = useState(false);

  const isChildAccountEnabled = ['1', 1].includes(
    useConfigService('ENABLE_CHILD_ACCOUNT_CHANGES'),
  );
  const isSquareBenefitsEnabled = ['1', 1].includes(
    useConfigService('ENABLE_SQUARE_BENEFITS'),
  );
  const showV2PriceBook = !!useConfigService('ENABLE_V2_PRICEBOOK');
  const isRegistrationEntryPointEnabled = !!useConfigService(
    'ENABLE_V2_REGISTRATION_ENTRY_POINT',
  );

  const getRewardsAndOffersData = useCallback(async () => {
    return accountApi
      .rewardsAndOffersDetails()
      .then((data) => {
        if (!data?.customerInfo) {
          newrelic.addPageAction(PRICING_BOOK_PAGE_LOAD, {
            result: 'error',
            errorCode: 'MISSING_CUSTOMER_INFO',
            errorMessage: 'API response missing customerInfo',
            version: showV2PriceBook ? 'v2' : 'v1',
          });
          setGeneralError(ERR_PRICING_BOOK);
        }

        setRewardsAndOffers(data);
        setIsChildAccount(
          !!data?.customerInfo?.isChildAccount && isChildAccountEnabled,
        );
      })
      .catch((error: AxiosError | CredentialsMissingError) => {
        setGeneralError(ERR_PRICING_BOOK);

        if (error instanceof CredentialsMissingError) {
          newrelic.addPageAction(PRICING_BOOK_PAGE_LOAD, {
            result: 'error',
            errorCode: 'NO_CREDENTIALS',
            errorMessage: 'User credentials (svocID/userID) are missing',
            version: showV2PriceBook ? 'v2' : 'v1',
          });
          return;
        }

        const { errorCode, errorMessage, httpStatus } =
          extractRewardsError(error);

        newrelic.addPageAction(PRICING_BOOK_PAGE_LOAD, {
          result: 'error',
          errorCode,
          errorMessage,
          httpStatus,
          version: showV2PriceBook ? 'v2' : 'v1',
        });
      });
  }, [isChildAccountEnabled, showV2PriceBook]);

  return (
    <RewardsAndOffersContext.Provider
      value={React.useMemo(
        () => ({
          rewardsAndOffers,
          isChildAccount,
          getRewardsAndOffersData,
          isLoading,
          setIsLoading,
          generalError,
          setGeneralError,
          isSquareBenefitsEnabled,
          showV2PriceBook,
          isRegistrationEntryPointEnabled,
        }),
        [
          generalError,
          isChildAccount,
          isLoading,
          isSquareBenefitsEnabled,
          rewardsAndOffers,
          getRewardsAndOffersData,
          showV2PriceBook,
          isRegistrationEntryPointEnabled,
        ],
      )}
    >
      {children}
    </RewardsAndOffersContext.Provider>
  );
};

export { RewardsAndOffersProvider, RewardsAndOffersContext };
